import React, { useEffect, useState } from "react";
import { goToCheckout } from "@/lib/checkout";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, getB2CToken } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import { labelFor, statusCls } from "@/lib/categories";
import { FindoSpinner, WaitingNotice, PageLoader } from "@/components/FindoSpinner";
import LostResponsesReview, { ContactCard } from "@/components/LostResponsesReview";
import { Button } from "@/components/ui/button";
import FindoConfirmDialog from "@/components/FindoConfirmDialog";
import ContributionSlider from "@/components/ContributionSlider";
import { ArrowLeft, Check, Circle, PackageSearch, MapPin, Download, CheckCircle2, Handshake, XCircle, ShieldCheck } from "lucide-react";

export default function FinderTrack() {
  const { adId } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useB2C();
  const [ad, setAd] = useState(null);
  const [mode, setMode] = useState("found");
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    try {
      const r = await api(`/b2c/found-ads/${adId}`, { b2c: true });
      setMode("found"); setAd(r); setVerified(!!r.restitution_verified);
    } catch {
      try {
        const r = await api(`/b2c/finder-track/${adId}`, { b2c: true });
        setMode("finder"); setAd(r); setVerified(!!r.restitution_verified);
      } catch {
        try {
          const r = await api(`/b2c/claimed-ads/${adId}`, { b2c: true });
          setMode("claimant"); setAd(r); setVerified(!!r.restitution_verified);
        } catch { setNotFound(true); }
      }
    }
  };

  async function verifyCode() {
    if (!code.trim()) { toast.error(t("ft.codeNeeded")); return; }
    setVerifying(true);
    try {
      const base = mode === "finder" ? `/b2c/finder-track/${adId}` : `/b2c/found-ads/${adId}`;
      await api(`${base}/verify-code`, { method: "POST", b2c: true, body: { code: code.trim() } });
      setVerified(true);
      toast.success(t("ft.codeOk"));
    } catch (e) { toast.error(e.message); } finally { setVerifying(false); }
  }

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/mon-compte"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId, user, authLoading]);

  const [confirmAction, setConfirmAction] = useState(null);
  const closeCase = () => setConfirmAction("close");
  const cancelReport = () => setConfirmAction("cancel");
  const cancelText = t("ft.cancelConfirm") === "ft.cancelConfirm" ? "Annuler votre signalement ? Le propriétaire de l'objet en sera informé." : t("ft.cancelConfirm");

  async function doCloseCase() {
    setBusy(true);
    try {
      const url = mode === "finder" ? `/b2c/finder-track/${adId}/close` : `/b2c/found-ads/${adId}/close`;
      await api(url, { method: "POST", b2c: true });
      toast.success(t(mode === "finder" && ad?.flow_v2 ? "fa.followupClosed" : "ft.closed"));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function doCancelReport() {
    setBusy(true);
    try {
      await api(`/b2c/finder-track/${adId}/cancel`, { method: "POST", b2c: true });
      toast.success(t("ft.cancelDone") === "ft.cancelDone" ? "Signalement annulé." : t("ft.cancelDone"));
      navigate("/mon-compte");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function downloadCertificate() {
    try {
      const path = mode === "finder" ? `finder-track/${adId}/certificate.pdf` : `found-ads/${adId}/certificate.pdf`;
      const res = await fetch(`${API}/b2c/${path}`, {
        headers: { Authorization: `Bearer ${getB2CToken()}` },
      });
      if (!res.ok) throw new Error(t("mc2.error").replace("{status}", res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `certificat-restitution-${adId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="finder-track-notfound">
        <PackageSearch size={40} className="mx-auto text-slate-300" />
        <p className="mt-4 text-slate-500">{t("an.none")}</p>
        <Link to="/mon-compte"><Button variant="outline" className="mt-6 rounded-full">{t("ft.backAccount")}</Button></Link>
      </div>
    );
  }
  if (!ad) return <PageLoader label={t("c.loading")} className="max-w-2xl mx-auto py-32" />;

  const isClosed = ad.status === "closed" || ad.status === "resolved" || ad.status === "restituted";
  const v2Finder = mode === "finder" && ad.flow_v2;
  const hasContact = ad.flow_v2 ? (ad.review_status === "approved" || isClosed) : ((ad.contact_count || 0) > 0 || ad.accepted || isClosed);
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(lang, { day: "2-digit", month: "short", year: "numeric" }) : null);
  const steps = [
    { key: "published", label: t("ft.stepPublished"), done: true, date: fmt(ad.published_at) },
    { key: "inprogress", label: t("ft.stepInProgress"), done: hasContact, date: fmt(ad.in_progress_at) },
    { key: "returned", label: t(v2Finder && ad.finder_closed ? "fa.followupClosed" : "ft.stepReturned"), done: isClosed, date: fmt(ad.returned_at) },
  ];

  return (
    <div className="bg-background min-h-[70vh]">
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="finder-track-page">
        <FindoConfirmDialog open={!!confirmAction} testid={`finder-track-${confirmAction || "confirm"}`}
          text={confirmAction === "close" ? t(v2Finder ? "fa.closeFollowupConfirm" : "ft.closeConfirm") : cancelText}
          okLabel={confirmAction === "close" ? t(v2Finder ? "fa.closeFollowup" : "ft.closeBtn") : t("ft.cancelReport")} danger={confirmAction === "cancel"}
          onClose={() => setConfirmAction(null)}
          onConfirm={() => { const a = confirmAction; setConfirmAction(null); a === "close" ? doCloseCase() : doCancelReport(); }} />
        <h1 className="mt-2 font-display text-3xl font-semibold text-findo-navy" data-testid="finder-track-title">{t("ft.title")}</h1>
        <p className="mt-2 text-slate-500">{t("ft.subtitle")}</p>

        <div className="mt-8 bg-white rounded-3xl border border-border card-shadow overflow-hidden">
          <div className="flex items-center gap-4 p-6 border-b border-border">
            {ad.image_base64 && <img src={ad.image_base64} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-teal-600 font-semibold">{labelFor(ad.category)}</p>
              <p className="font-semibold text-slate-800 truncate">{ad.description}</p>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5"><MapPin size={13} /> {ad.lost_place || "—"}</p>
            </div>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(ad.status)}`} data-testid="finder-track-status">{t(`st.${ad.status}`)}</span>
          </div>

          {/* Restitution timeline */}
          <div className="p-6" data-testid="finder-track-timeline">
            <div className="space-y-4">
              {steps.map((s, i) => (
                <div key={s.key} className="flex items-start gap-3">
                  <div className={`mt-0.5 h-7 w-7 rounded-full grid place-items-center shrink-0 ${s.done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                    {s.done ? <Check size={15} /> : <Circle size={13} />}
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${s.done ? "text-findo-navy" : "text-slate-400"}`}>{s.label}</p>
                    {s.date && s.done && <p className="text-xs text-slate-400 mt-0.5" data-testid={`finder-step-date-${s.key}`}>{s.date}</p>}
                    {i < steps.length - 1 && <div className={`ml-[13px] mt-1 h-4 w-px ${s.done ? "bg-emerald-300" : "bg-slate-200"}`} />}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-border bg-slate-50/50">
            {mode === "found" && ad.flow_v2 && !isClosed ? (
              <div className="space-y-3" data-testid="found-v2-actions">
                <LostResponsesReview ad={ad} reload={load} />
                {ad.contacts_unlocked && (
                  <Button onClick={closeCase} disabled={busy} className="w-full rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 text-white" data-testid="finder-track-close-btn">
                    <Handshake size={16} className="mr-1.5" /> {busy ? <FindoSpinner size={16} /> : t("ft.closeBtn")}
                  </Button>
                )}
              </div>
            ) : mode === "claimant" && ad.flow_v2 && !ad.contacts_unlocked && !isClosed ? (
              <ClaimantV2Actions ad={ad} t={t} adId={adId} />
            ) : mode === "claimant" ? (
              <div className="space-y-4" data-testid="claimant-track-actions">
                {ad.contacts_unlocked && ad.finder ? (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="claimant-finder-contacts">
                    <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">{t("ft.finderContactsTitle")}</p>
                    <p className="mt-2 text-slate-800 font-semibold text-sm">{ad.finder.name}</p>
                    {ad.finder.email && <p className="text-slate-600 text-sm mt-0.5"><a href={`mailto:${ad.finder.email}`} className="hover:text-findo-navy underline">{ad.finder.email}</a></p>}
                    {ad.finder.phone && <p className="text-slate-600 text-sm mt-0.5"><a href={`tel:${ad.finder.phone}`} className="hover:text-findo-navy underline">{ad.finder.phone}</a></p>}
                  </div>
                ) : !isClosed ? (
                  <div className="rounded-xl bg-findo-navy/[0.04] border border-findo-navy/15 p-4" data-testid="claimant-unlock-hint">
                    <p className="text-sm font-semibold text-findo-navy">{t("ft.claimUnlockHint")}</p>
                    <p className="text-xs text-slate-500 mt-1">{t("ft.claimUnlockHintSub")}</p>
                  </div>
                ) : null}
                {ad.accepted && ad.restitution_code && !isClosed && (
                  <div className="rounded-xl bg-white border border-emerald-200 p-4" data-testid="claimant-restitution-code">
                    <p className="text-xs font-semibold text-emerald-800">{t("ft.claimCodeTitle")}</p>
                    <p className="mt-1.5 text-2xl font-extrabold tracking-[0.3em] font-mono text-findo-navy text-center bg-emerald-50 rounded-lg border border-emerald-200 py-2" data-testid="claimant-restitution-code-value">{ad.restitution_code}</p>
                    <p className="text-xs text-emerald-700 mt-1.5">{t("ft.claimCodeHelp")}</p>
                  </div>
                )}
                {isClosed && (
                  <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600" data-testid="claimant-track-closed">
                    <CheckCircle2 size={16} /> {t("ft.claimDone")}
                  </p>
                )}
              </div>
            ) : mode === "finder" && ad.flow_v2 && (!isClosed || ad.decided_by === "owner" || (ad.review_status === "approved" && !ad.finder_closed)) ? (
              <FinderV2Actions ad={ad} t={t} busy={busy} onCancel={cancelReport} onReload={load} />
            ) : ad.blocked_other ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center" data-testid="finder-track-blocked">
                <p className="text-sm font-semibold text-amber-800">{t("ft.inProgressOther")}</p>
                <p className="text-xs text-amber-700 mt-1">{t("ft.inProgressOtherHelp")}</p>
              </div>
            ) : !isClosed ? (
              <div className="space-y-4">
                {mode === "found" && ad.restitution_code && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="found-restitution-code">
                    <p className="text-xs font-semibold text-emerald-800">{t("ft.restitutionCodeTitle")}</p>
                    <p className="mt-1.5 text-2xl font-extrabold tracking-[0.3em] font-mono text-findo-navy text-center bg-white rounded-lg border border-emerald-200 py-2" data-testid="found-restitution-code-value">{ad.restitution_code}</p>
                    <p className="text-xs text-emerald-700 mt-1.5">{t("ft.foundCodeHelp")}</p>
                  </div>
                )}
                <div data-testid="finder-track-code-block">
                  {mode === "finder" && ad.restitution_code && (
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 mb-3" data-testid="finder-restitution-code">
                      <p className="text-xs font-semibold text-emerald-800">
                        {t("ft.finderCodeShownTitle") === "ft.finderCodeShownTitle" ? "Code de validation du propriétaire" : t("ft.finderCodeShownTitle")}
                      </p>
                      <p className="mt-1.5 text-2xl font-extrabold tracking-[0.3em] font-mono text-findo-navy text-center bg-white rounded-lg border border-emerald-200 py-2" data-testid="finder-restitution-code-value">{ad.restitution_code}</p>
                      <p className="text-xs text-emerald-700 mt-1.5">
                        {t("ft.finderCodeShownHelp") === "ft.finderCodeShownHelp" ? "Le propriétaire a payé et vous a choisi. Recopiez ce code ci-dessous pour confirmer la restitution — inutile de consulter votre email." : t("ft.finderCodeShownHelp")}
                      </p>
                    </div>
                  )}
                  <label className="block text-sm font-semibold text-findo-navy mb-1.5">{t("ft.codeLabel")}</label>
                  <div className="flex gap-2 w-full">
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      disabled={verified}
                      placeholder={t("ft.codePlaceholder")}
                      className="flex-1 min-w-0 h-11 rounded-full border border-border px-4 text-sm tracking-widest disabled:bg-slate-100 disabled:text-slate-400"
                      data-testid="finder-track-code-input"
                    />
                    <Button onClick={verifyCode} disabled={verifying || verified} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90 text-white shrink-0 px-4 whitespace-nowrap" data-testid="finder-track-verify-btn">
                      {verified ? <><Check size={16} className="mr-1" /> {t("ft.codeVerified")}</> : (verifying ? "…" : t("ft.verifyBtn"))}
                    </Button>
                  </div>
                  {verified && <p className="mt-2 text-xs font-semibold text-emerald-600 flex items-center gap-1.5" data-testid="finder-track-code-ok"><CheckCircle2 size={13} /> {t("ft.codeOk")}</p>}
                </div>
                <Button onClick={closeCase} disabled={busy || !verified} className="w-full rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 text-white disabled:opacity-40 disabled:cursor-not-allowed" data-testid="finder-track-close-btn">
                  <Handshake size={16} className="mr-1.5" /> {busy ? <FindoSpinner size={16} /> : t("ft.closeBtn")}
                </Button>
                {!verified && <p className="text-xs text-slate-400 text-center">{t("ft.closeLockedHint")}</p>}
                {mode === "finder" && (
                  ad.can_cancel === false ? (
                    <div className="rounded-full bg-slate-100 border border-border px-4 py-2.5 text-center" data-testid="finder-track-cancel-locked">
                      <p className="text-xs font-semibold text-slate-500">{t("ft.cancelLocked")}</p>
                    </div>
                  ) : (
                    <Button onClick={cancelReport} disabled={busy} variant="ghost"
                      className="w-full rounded-full font-semibold text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="finder-track-cancel-btn">
                      {busy ? <FindoSpinner size={16} /> : (t("ft.cancelReport") === "ft.cancelReport" ? "Annuler mon signalement" : t("ft.cancelReport"))}
                    </Button>
                  )
                )}
              </div>
            ) : (
              <div className="space-y-3" data-testid="finder-track-closed">
                <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                  <CheckCircle2 size={16} /> {t(v2Finder && ad.finder_closed ? "fa.followupClosedText" : "ft.doneMsg")}
                </p>
                {(mode === "found" || mode === "finder") && (
                  <Button onClick={downloadCertificate} variant="outline" className="w-full rounded-full font-semibold border-teal-600/30 text-teal-700 hover:bg-teal-50" data-testid="finder-track-certificate-btn">
                    <Download size={16} className="mr-1.5" /> {t("mc.certificate")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        {v2Finder && ad.review_status === "approved" && !ad.finder_closed && (
          <Button onClick={closeCase} disabled={busy} className="mt-6 w-full rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 text-white" data-testid="finder-close-followup-btn">
            <Handshake size={16} className="mr-1.5" /> {busy ? <FindoSpinner size={16} /> : t("fa.closeFollowup")}
          </Button>
        )}
      </div>
    </div>
  );
}

const SAFETY_TIPS = ["fa.safety1", "fa.safety2", "fa.safety3"];

function SafetyTips({ ad, t, onDone }) {
  const [busy, setBusy] = useState(false);
  async function ack() {
    setBusy(true);
    try { await api(`/b2c/finder-track/${ad.id}/safety-ack`, { method: "POST", b2c: true }); onDone?.(); }
    catch (e) { toast.error(e.message); setBusy(false); }
  }
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="finder-safety-tips">
      <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5"><ShieldCheck size={16} /> {t("fa.safetyTitle")}</p>
      <ul className="space-y-2">
        {SAFETY_TIPS.map((k) => (
          <li key={k} className="flex items-start gap-2 text-sm text-amber-900/90"><Check size={14} className="mt-1 shrink-0" /> <span>{t(k)}</span></li>
        ))}
      </ul>
      <Button onClick={ack} disabled={busy} className="w-full rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90 text-white" data-testid="finder-safety-ack-btn">
        {busy ? <FindoSpinner size={16} /> : t("fa.safetyOk")}
      </Button>
    </div>
  );
}

const DECL_ROWS = [["fa.fDate", "lost_date"], ["fa.fPlace", "lost_place"], ["fa.fCity", "city"], ["fa.fCircumstances", "circumstances"],
  ["fa.fColor", "color"], ["fa.fCondition", "condition"], ["fa.fBrand", "brand"], ["fa.fQuantity", "quantity"]];

function OwnerDeclarationCompare({ ad, t, onDone }) {
  const d = ad.owner_declaration || {};
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  async function decide(accept) {
    setBusy(true);
    try {
      await api(`/b2c/finder-track/${ad.id}/decide`, { method: "POST", b2c: true, body: { accept } });
      toast.success(t(accept ? "fa.youConfirmed" : "fa.youDeclined"));
      onDone?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <div className="space-y-3" data-testid="finder-owner-declaration">
      <div>
        <p className="text-sm font-semibold text-findo-navy">{t("fa.colOwner")}</p>
        <p className="text-xs text-slate-500">{t("fa.compareOwnerHint")}</p>
      </div>
      {(d.photo || d.photo_2) && (
        <div className={d.photo && d.photo_2 ? "grid grid-cols-2 gap-2" : ""}>
          {d.photo && <img src={d.photo} alt="" className="h-40 w-full object-cover rounded-xl border border-border" data-testid="owner-decl-photo" />}
          {d.photo_2 && <img src={d.photo_2} alt="" className="h-40 w-full object-cover rounded-xl border border-border" data-testid="owner-decl-photo2" />}
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-border bg-white text-sm">
        {d.description && <div className="p-2.5 font-medium text-slate-800" data-testid="owner-decl-description">{d.description}</div>}
        {DECL_ROWS.map(([label, k]) => (
          <div key={k} className="grid grid-cols-[1fr_1.6fr] border-t border-border first:border-t-0">
            <span className="p-2.5 text-xs font-semibold text-slate-500">{t(label)}</span>
            <span className="p-2.5 text-slate-800 break-words" data-testid={`owner-decl-${k}`}>{d[k] ?? "—"}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button onClick={() => setConfirm("yes")} disabled={busy} className="flex-1 rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid="finder-decide-yes">
          <CheckCircle2 size={15} className="mr-1.5" /> {t("fa.itsThisItem")}
        </Button>
        <Button onClick={() => setConfirm("no")} disabled={busy} variant="outline" className="flex-1 rounded-full font-semibold text-slate-600" data-testid="finder-decide-no">
          <XCircle size={15} className="mr-1.5" /> {t("fa.notThisItem")}
        </Button>
      </div>
      <FindoConfirmDialog open={!!confirm} testid="finder-decide-confirm"
        text={t(confirm === "yes" ? "fa.confirmIsItem" : "fa.confirmNotItem")}
        okLabel={t(confirm === "yes" ? "fa.itsThisItem" : "fa.notThisItem")} danger={confirm === "no"}
        onClose={() => setConfirm(null)} onConfirm={() => { const c = confirm; setConfirm(null); decide(c === "yes"); }} />
    </div>
  );
}

function ContributionOffer({ ad, t }) {
  const [amount, setAmount] = useState(10);
  const [busy, setBusy] = useState(false);
  async function pay() {
    setBusy(true);
    try {
      const res = await api(`/b2c/finder-track/${ad.id}/contribution`, { method: "POST", b2c: true, body: { amount, origin_url: window.location.origin } });
      if (res?.url) { goToCheckout(res.url); return; }
      toast.success(t("fa.contribPending"));
      window.location.reload();
    } catch (e) { toast.error(e.message); setBusy(false); }
  }
  return (
    <div className="rounded-2xl border border-border p-4 space-y-3" data-testid="finder-contribution-offer">
      <ContributionSlider value={amount} onChange={setAmount} />
      {amount > 0 && (
        <Button onClick={pay} disabled={busy} variant="outline" className="w-full rounded-full font-semibold" data-testid="finder-contribution-pay">
          {busy ? <FindoSpinner size={16} /> : `${t("fa.contribute")} · ${amount} €`}
        </Button>
      )}
    </div>
  );
}

function FinderV2Actions({ ad, t, busy, onCancel, onReload }) {
  const contrib = ad.contribution_eur > 0 && ["paid", "pledged"].includes(ad.contribution_status) && (
    <p className={`text-xs font-semibold ${ad.contribution_status === "paid" ? "text-emerald-600" : "text-amber-600"}`} data-testid="finder-v2-contribution">
      {ad.contribution_status === "paid" ? t("fa.contribPaid") : t("fa.contribPending")} · {ad.contribution_eur} €
    </p>
  );
  if (ad.review_status === "rejected") {
    const byOwner = ad.decided_by === "owner";
    return (
      <div className="rounded-xl bg-slate-100 border border-border p-4" data-testid="finder-v2-rejected">
        <p className="text-sm font-semibold text-slate-700">{t(byOwner ? "fa.ownerRejectedPhoto" : "fa.youDeclined")}</p>
        <p className="text-xs text-slate-500 mt-1">{t(byOwner ? "fa.ownerRejectedPhotoText" : "fa.youDeclinedText")}</p>
      </div>
    );
  }
  if (ad.review_status === "approved") {
    return (
      <div className="space-y-3" data-testid="finder-v2-approved">
        <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={16} /> {t("fa.youConfirmed")}</p>
        <p className="text-xs text-slate-500">{t("fa.approvedFinderText")}</p>
        {!ad.safety_ack ? <SafetyTips ad={ad} t={t} onDone={onReload} />
          : ad.owner && <ContactCard title={t("fa.ownerContact")} c={ad.owner} testid="finder-v2-owner-contact" />}
        {ad.owner_closed && <p className="text-xs font-semibold text-slate-500" data-testid="finder-v2-owner-closed">{t("fa.ownerClosedNote")}</p>}
        {contrib}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="finder-v2-pending">
      {ad.blocked_other ? (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center" data-testid="finder-track-blocked">
          <p className="text-sm font-semibold text-amber-800">{t("ft.inProgressOther")}</p>
        </div>
      ) : ad.owner_photo_status === "pending" ? (
        <WaitingNotice title={t("fa.ownerReviewingPhoto")} text={t("fa.ownerReviewingPhotoText")} testid="finder-v2-photo-review" />
      ) : (
        <>
          <OwnerDeclarationCompare ad={ad} t={t} onDone={onReload} />
          {ad.contribution_available && !["paid", "pledged"].includes(ad.contribution_status) && <ContributionOffer ad={ad} t={t} />}
        </>
      )}
      {contrib}
      <Button onClick={onCancel} disabled={busy} variant="ghost" className="w-full rounded-full font-semibold text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="finder-track-cancel-btn">
        {busy ? <FindoSpinner size={16} /> : t("ft.cancelReport")}
      </Button>
    </div>
  );
}

function ClaimantV2Actions({ ad, t, adId }) {
  const [busy, setBusy] = useState(false);
  async function unlock() {
    setBusy(true);
    try {
      const res = await api(`/b2c/claimed-ads/${adId}/unlock`, { method: "POST", b2c: true, body: { origin_url: window.location.origin } });
      if (res?.url) { goToCheckout(res.url); return; }
      toast.success(t("fa.claimUnlockedShort"));
      window.location.reload();
    } catch (e) { toast.error(e.message); setBusy(false); }
  }
  if (ad.review_status === "rejected") {
    return (
      <div className="rounded-xl bg-slate-100 border border-border p-4" data-testid="claimant-v2-rejected">
        <p className="text-sm font-semibold text-slate-700">{t("fa.claimRejectedShort")}</p>
        <p className="text-xs text-slate-500 mt-1">{t("fa.claimRejectedText")}</p>
      </div>
    );
  }
  if (ad.review_status === "approved") {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="claimant-v2-approved">
        <p className="text-sm font-semibold text-emerald-800">{t("fa.claimApprovedShort")}</p>
        <p className="text-xs text-emerald-700 mt-1">{t("fa.claimUnlockText")}</p>
        <Button onClick={unlock} disabled={busy} className="mt-3 w-full rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="claimant-v2-unlock-btn">
          {busy ? <FindoSpinner size={16} /> : ad.unlock_free ? t("fa.unlockFree") : `${t("fa.unlockBtn")} · ${String(ad.unlock_fee_ttc).replace(".", ",")} € ${t("fa.ttc")}`}
        </Button>
      </div>
    );
  }
  return <WaitingNotice title={t("fa.waitFinderDecision")} text={t("fa.waitClaimText")} testid="claimant-v2-waiting" />;
}
